import { WorkflowOutput } from "@webuild-ai/admin/models/components";
import React, { useEffect, useState } from "react";
import { useBrand, useRBAC } from "@webuild-ai/react-auth";

import { logger } from "@/telemetry";
import { WorkflowCard } from "@/components/WorkflowCard/WorkflowCard";
import { NewWorkflowCard } from "@/components/WorkflowCard/NewWorkflowCard";
import { PageTitle } from "@/components/UI/PageTitle";

import { useWebuildAPIClient } from "../../providers/WeBuildClientProvider";

export const WorkflowIndexPage: React.FC = () => {
  const { adminClient } = useWebuildAPIClient();
  const [workflows, setWorkflows] = useState<WorkflowOutput[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { brand } = useBrand();
  const { hasPermission } = useRBAC();
  const fetchWorkflows = async () => {
    if (adminClient) {
      try {
        setIsLoading(true);
        const workflows = await adminClient.workflows.listWorkflows({});

        setWorkflows(workflows);
      } catch (error) {
        logger.error("Error fetching workflows", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleDeleteWorkflow = async (id: string) => {
    if (adminClient) {
      try {
        await adminClient.workflows.deleteWorkflow({ workflowId: id });
        await fetchWorkflows(); // Refresh the list after deletion
      } catch (error) {
        logger.error("Error deleting workflow", error);
      }
    }
  };

  const handleCloneWorkflow = async (id: string) => {
    if (adminClient) {
      try {
        // Get the workflow to clone
        const originalWorkflow = await adminClient.workflows.getWorkflow({
          workflowId: id,
        });

        // Create a cloned workflow with v2 appended to the id
        const clonedWorkflowId = `${originalWorkflow.id}_v2`;
        const clonedWorkflow = {
          ...originalWorkflow,
          id: clonedWorkflowId,
          name: `${originalWorkflow.name} (Copy)`,
        };

        // Create the new workflow using updateWorkflow (which handles both create and update)
        await adminClient.workflows.updateWorkflow({
          workflowId: clonedWorkflowId,
          workflowInput: clonedWorkflow,
        });

        await fetchWorkflows(); // Refresh the list after cloning
      } catch (error) {
        logger.error("Error cloning workflow", error);
      }
    }
  };

  useEffect(() => {
    fetchWorkflows();
  }, [adminClient]);

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="text-center py-8" data-id="workflows-loading">
          Loading workflows...
        </div>
      );
    }

    return (
      <div
        className="gap-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
        data-id="workflows-grid"
      >
        {workflows
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((workflow) => (
            <WorkflowCard
              key={workflow.id}
              canDelete={
                hasPermission("*", "admin") || hasPermission(`workflows:${workflow.id}`, "delete")
              }
              canEdit={
                hasPermission("*", "admin") || hasPermission(`workflows:${workflow.id}`, "write")
              }
              data-id={`workflow-card-${workflow.id}`}
              description={workflow.description}
              icon={workflow.icon}
              id={workflow.id}
              title={workflow.name}
              onClone={handleCloneWorkflow}
              onDelete={handleDeleteWorkflow}
            />
          ))}
        {hasPermission("workflows", "create") && <NewWorkflowCard data-id="new-workflow-card" />}
      </div>
    );
  };

  return (
    <div className="p-4" data-id="workflow-index-page">
      <PageTitle
        centered
        dataId="workflow-title"
        icon="solar:widget-2-outline"
        img={`/brands/${brand.prefix || "webuild"}/playbook.svg`}
        subtitle="Create and manage your AI workflows"
        title={brand.menuItems?.["Workflows"]?.name || "Workflows"}
      />
      {renderContent()}
    </div>
  );
};
